import { Trans, useTranslation } from 'react-i18next';

import type { MoretonBayBillingInitializer } from '@ctct/application-layer';
import { BillingStrategy, createResolvedBillingStrategy } from '@ctct/domain-layer';
import { Card, Heading } from '@ctct/payflow-ui';
import { OrderSummary, PayflowTopBar } from '@ctct/presentation-layer';
import {
  AlertWidget,
  BillingInfoFormWidget,
  PaymentMethodsWidget,
  UpdateBillingWidget,
  useAlertWidget,
  usePaymentMethodsWidget,
  useRouterNonUiWidget,
  useUpdateBillingWidget,
  isApplePayParamEnabled,
  useBillingInfoFormWidget,
  isStripeParamEnabled,
  isStripePhase2ParamEnabled,
} from '@ctct/react-app-layer';
import {
  createPaymentMethodsHooks,
  createAlertHooks,
  useBillingInfoPageStore,
  createUpdateBillingHooks,
  createBillingInfoFormHooks,
} from '@ctct/state-client-layer';
import { usePartnerSummary, usePaymentStrategy } from '@ctct/state-server-layer';

const alertHooks = createAlertHooks(useBillingInfoPageStore);
const paymentMethodsHooks = createPaymentMethodsHooks(useBillingInfoPageStore);
const updateBillingHooks = createUpdateBillingHooks(useBillingInfoPageStore);
const billingInfoFormHooks = createBillingInfoFormHooks(useBillingInfoPageStore);

const paymentMethodsExternalHooks = {
  useRatingCallPayload: () => null,
};

const routerNonUiExternalHooks = {
  useIsHidePromoCodesEnabled: () => false, // no promo code application on this page
};

interface Props {
  initializer: MoretonBayBillingInitializer;
}

export const MoretonBayBillingPage = ({ initializer }: Props) => {
  const { t } = useTranslation();
  const isApplePayEnabled = isApplePayParamEnabled();
  const isStripeEnabled = isStripeParamEnabled() ?? false;
  const isStripePhase2Enabled = isStripePhase2ParamEnabled();
  const billingStrategy = createResolvedBillingStrategy(
    isStripeEnabled ? BillingStrategy.Stripe : BillingStrategy.InHouse
  );
  const paymentStrategy = usePaymentStrategy({
    billingStrategyQuery: billingStrategy,
  });

  const { data: partnerSummary, isLoading: isLoadingPartnerSummary } = usePartnerSummary();

  const skipFormPrefill = partnerSummary?.getIsExternalPartner() ?? false;

  const orderSummary = (
    <OrderSummary
      copies={{ heading: t('moreton-bay-page.order-summary') }}
      orderItems={[
        {
          id: 'moreton-bay-lite-plan',
          variant: 'bold',
          mainLabel: t('moreton-bay-page.plan-name'),
          secondaryLabels: [],
          priceLabel: (
            <span className="moreton-bay-page_price">
              <Trans i18nKey="moreton-bay-page.price" components={{ strong: <strong />, span: <span /> }} />
            </span>
          ),
        },
        {
          id: 'moreton-bay-lite-plan-description',
          variant: 'bold',
          mainLabel: null,
          secondaryLabels: [t('moreton-bay-page.plan-description')],
          priceLabel: null,
        },
      ]}
      injectedMiddleComponent={
        <div className="moreton-bay-page_features">
          <p className="moreton-bay-page_features-list-header">{t('moreton-bay-page.features-heading')}</p>
          <ul className="moreton-bay-page_features-list">
            <li>{t('moreton-bay-page.feature1')}</li>
            <li>{t('moreton-bay-page.feature2')}</li>
            <li>{t('moreton-bay-page.feature3')}</li>
            <li>{t('moreton-bay-page.feature4')}</li>
          </ul>
        </div>
      }
      footerItems={[]}
    />
  );

  useRouterNonUiWidget({
    initializer,
    isStripeEnabled: isStripeEnabled ?? false,
    isStripePhase2Enabled: isStripePhase2Enabled ?? false,
    externalHooks: routerNonUiExternalHooks,
  });

  const alertWidgetProps = useAlertWidget({
    initializer,
    pageStore: useBillingInfoPageStore,
    hooks: alertHooks,
  });

  const paymentMethodsWidgetProps = usePaymentMethodsWidget({
    initializer,
    pageStore: useBillingInfoPageStore,
    hooks: paymentMethodsHooks,
    isApplePayEnabled,
    billingStrategy,
    paymentStrategy,
    disableApplePayAsNewPaymentMethod: true,
    externalHooks: paymentMethodsExternalHooks,
  });

  const updateBillingWidgetProps = useUpdateBillingWidget({
    initializer,
    pageStore: useBillingInfoPageStore,
    hooks: updateBillingHooks,
  });

  const billingInfoFormWidgetProps = useBillingInfoFormWidget({
    initializer,
    pageStore: useBillingInfoPageStore,
    hooks: billingInfoFormHooks,
    billingStrategy,
    flags: { skipFormPrefill },
  });

  if (isLoadingPartnerSummary) {
    return null;
  }

  return (
    <div className="moreton-bay-page">
      <PayflowTopBar nonInteractive />
      <AlertWidget {...alertWidgetProps} />
      <div className="moreton-bay-page-container">
        <div className="moreton-bay-page_heading-container">
          <Heading level="h3">{t('moreton-bay-page.heading')}</Heading>
          <p>{t('moreton-bay-page.sub-heading')}</p>
        </div>
        <div className="moreton-bay-page-content">
          <Card className="moreton-bay-page_mobile-order-summary">{orderSummary}</Card>
          <Card className="moreton-bay-page_payment-methods">
            <PaymentMethodsWidget {...paymentMethodsWidgetProps} />
            <BillingInfoFormWidget {...billingInfoFormWidgetProps} />
          </Card>

          <Card className="moreton-bay-page_summary">
            <div className="moreton-bay-page_desktop-order-summary">{orderSummary}</div>
            <div className="moreton-bay-page_disclaimer">
              <Trans i18nKey="moreton-bay-page.disclaimer" components={{ strong: <strong /> }} />
            </div>
            <UpdateBillingWidget {...updateBillingWidgetProps} version="moreton-bay" />
          </Card>
        </div>
      </div>
    </div>
  );
};
